import { FC, useEffect } from 'react';
import { css } from '@emotion/react';
import { Category, Sentiment } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import dayjs from 'dayjs';
import { useInView } from 'react-intersection-observer';
import {
  CallIntelChip,
  generateTrackingId,
  TrackingOptions,
  useCallIntelDefaultsShallowStore,
} from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { TRACKING_BASE } from '../../constants';
import { TeaserCard } from './teaser-card';

interface PatientInsightTeaserVariantBProps {
  locationId?: string;
  onViewDemo?: () => void;
  onDismiss?: () => void;
}

const TRACKING_CONFIG: TrackingOptions = {
  ...TRACKING_BASE,
  prefix: 'call-intel-patient-insight-teaser',
  abTestGroup: 'variant-b',
};

export const PatientInsightTeaserVariantB: FC<PatientInsightTeaserVariantBProps> = ({
  locationId,
  onViewDemo,
  onDismiss,
}) => {
  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView) {
      pendo.track(generateTrackingId({ ...TRACKING_CONFIG, context: 'visible' }));
    }
  }, [inView]);

  return (
    <TeaserCard innerRef={ref} data-testid='patient-insight-teaser' data-variant='b'>
      <TeaserCard.Header />
      <TeaserCard.Body>
        <TeaserCard.Description />
        <TeaserCard.FeatureChips />
        <MockLastCallCard />
        <TeaserCard.ActionBar
          variant='overlay'
          locationId={locationId}
          onViewDemo={onViewDemo}
          onDismiss={onDismiss}
          trackingOptions={TRACKING_CONFIG}
          css={css`
            margin-top: -${theme.spacing(2)};
            margin-left: -${theme.spacing(1)};
            margin-right: -${theme.spacing(1)};
          `}
        />
      </TeaserCard.Body>
    </TeaserCard>
  );
};

const MockLastCallCard: FC = () => {
  const { t } = useTranslation('callIntelInsights');
  const currentDate = dayjs().format('MMM D, YYYY');
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  return (
    <div css={styles.mockCard} data-testid='patient-insight-teaser-last-call-card'>
      <div css={styles.mockHeader}>
        <Icon name='phone-incoming-small' />
        <Text size='medium' weight='bold'>
          {t('Last Call')}
        </Text>
        <Text size='medium' color='subdued'>
          {currentDate}
        </Text>
      </div>

      <div css={[styles.mockAttributes, styles.blurredContent]}>
        <CallIntelChip
          chipVariants={chipVariants}
          dataKey={Sentiment.SENTIMENT_NEGATIVE}
          label={dataLabels.sentiments?.[Sentiment.SENTIMENT_NEGATIVE]}
        />
        <CallIntelChip
          chipVariants={chipVariants}
          dataKey={Category.CATEGORY_BILLING}
          label={dataLabels.categories?.[Category.CATEGORY_BILLING]}
        />
        <Text size='medium'>{t('1 Task')}</Text>
      </div>

      <Text size='medium' weight='bold' css={[styles.mockSummary, styles.blurredContent]}>
        {t(
          'Jane Doe, an existing patient, called frustrated about a $191 charge for a preventive exam that her insurance should have covered. She deman...'
        )}
      </Text>
    </div>
  );
};

// ── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  mockCard: css`
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.medium};
    padding: ${theme.spacing(1)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    background-color: ${theme.colors.white};
    margin-top: ${theme.spacing(1)};
  `,
  mockHeader: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  mockAttributes: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    flex-wrap: wrap;
  `,
  mockSummary: css`
    text-align: left;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `,
  blurredContent: css`
    filter: blur(3px);
    pointer-events: none;
    user-select: none;
  `,
};
